import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import Breadcrumbs from "../../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import CommonFilterModal from "../../../../components/Common/CommonFilterModal";
import DoctorsFilterOptions from "../../../../components/Common/CommonFilterOptions";
import NavBarTitles from "../../../../components/Common/TabBar";
import Deletepopup from "../../../../components/Modal/Deletepopup";

// Updated custom hook paths
import ListPageLayout from "../../../../components/Layout";
import useDebouncedSearch from "../../../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../../../components/Utils/hooks/useSortHandler";

import apiCall from "../../../../components/Api";
import Actions from "../../../../components/Common/Actions";
import FilledButton from "../../../../components/Common/Buttons/FilledButton";

export const DoctorCommonHeads = [
  {
    name: "Gender",
    navigatePath: "/doctors-gender",
  },
  {
    name: "Experience",
    navigatePath: "/doctors-experience",
  },
  {
    name: "Languages Known",
    navigatePath: "/doctors-language",
  },
  {
    name: "Medical Council",
    navigatePath: "/doctors-medical-council",
  },
  {
    name: "Clinic Speciality",
    navigatePath: "/doctors-clinic-speciality",
  },
  {
    name: "Online Specialization",
    navigatePath: "/online-specialization",
  },
  {
    name: "Inclinic Specialization",
    navigatePath: "/inclinic-specializations",
  },
];

const DoctorClinicSpeciality = () => {
  const navigate = useNavigate();
  const addNewClinicSpeciality = {
    id: "addnewpost",
    onClick: () => navigate("/doctors-clinic-speciality-create"),
    style: { width: "240px" },
    label: "Add New Clinic Speciality",
  };
  // Sorting
  const [sort, setSort] = useState("");
  const [sortName, setSortName] = useState("Newest First");
  const handleSortClick = useSortHandler(setSort, setSortName);
  const [clinicSpeciality, setClinicSpeciality] = useState([]);
  const [loader, setLoader] = useState(true);

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 })),
  );

  const getClinicSpeciality = async () => {
    const result = await apiCall(`clinic-speciality/list`, "POST");
    setClinicSpeciality(
      result.data.rows.map((data) => data?.clinic_speciality),
    );
  };

  const [clinicSplData, setClinicSplData] = useState([]);

  // Filter states
  const [selectedValues, setSelectedValues] = useState({
    experience: [],
    Status: [],
  });
  const [filteringKey, setFilteringKey] = useState([]);
  const [tempSelectedValues, setTempSelectedValues] = useState({
    experience: [],
    Status: [],
  });

  const [selectedCategory, setSelectedCategory] = useState("Clinic Speciality");
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);

  // Delete state
  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  // Pagination
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}clinic-speciality/list`,
      initialParams: {
        sort,
        searchText: search,
        selectedValues,
      },
    });

  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler(
    "clinic-speciality",
    fetchData,
    setShowDelete,
  );

  useEffect(() => {
    getClinicSpeciality();
    setLoader(true)
    fetchData();
  }, [pagination.skip, pagination.limit, sort, search, selectedValues]);

  const columns = [
    {
      title: "S.No",
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
      key: "serial",
      width: 80,
      fixed: "left",
    },
    {
      title: "Medical Council",
      dataIndex: "clinic_speciality",
      key: "clinic_speciality",
    },
    {
      title: "Status",
      width: 150,
      key: "status",
      render: (text, record, index) => (record.active ? "Active" : "InActive"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Actions
          isEdit={true}
          isView={true}
          onClickView={() =>
            navigate(`/doctors-clinic-speciality-view/${record._id}`)
          }
          isDelete={true}
          onClickDelete={() => {
            setDeleteId(record._id);
            setShowDelete(true);
          }}
          onClickEdit={() =>
            navigate(`/doctors-clinic-speciality-edit/${record._id}`)
          }
        />
      ),
    },
  ];

  const dataSource = records.map((el) => ({ ...el, key: el._id }));

  const filters = {
    "Clinic Speciality": clinicSpeciality, //
    Status: ["Active", "Inactive"],
  };

  const breadcrumbsList = useMemo(
    () => [
      {
        text: "Doctor",
        link: "/add-new-doctors",
      },
      {
        text: "Common Heads",
        link: "/doctors-clinic-speciality",
      },
      {
        text: "Clinic Speciality",
        link: "",
      },
    ],
    [],
  );

  useEffect(() => {
    records && setTimeout(() => setLoader(false));
  }, [records]);

  return (
    <ListPageLayout
      breadcrumbs={
        <Breadcrumbs
          className="healthsyMasterBreadCrumbs"
          breadcrumbsList={breadcrumbsList}
        />
      }
      addButton={<FilledButton props={addNewClinicSpeciality} />}
      titles={<NavBarTitles titles={DoctorCommonHeads} />}
      filters={
        <DoctorsFilterOptions
          limit={pagination.limit}
          setLimit={(val) =>
            setPagination((prev) => ({
              ...prev,
              limit: val,
              skip: 0,
              currentPage: 1,
            }))
          }
          search={search}
          onChange={debouncedSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          filter_records={() => {}}
          setFilterShow={() => setIsFilterModalVisible(true)}
        />
      }
      tableProps={{
        columns,
        dataSource,
      }}
      totalCount={totalCount}
      pagination={pagination}
      onPageChange={(selected) => handlePageChange({ selected })}
      loader={loader}
    >
      {/* Optional children like modals */}
      {showDelete && (
        <Deletepopup
          handleDelete={() => handleDelete(deleteId)}
          id={deleteId}
          handleClose={() => setShowDelete(false)}
        />
      )}

      <CommonFilterModal
        visible={isFilterModalVisible}
        onClose={() => setIsFilterModalVisible(false)}
        onApply={() => {
          setSelectedValues(tempSelectedValues);
          setIsFilterModalVisible(false);
          setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
          fetchData();
        }}
        onReset={() => {
          setTempSelectedValues({ Languages: [], Status: [] });
          setSelectedValues({ Languages: [], Status: [] });
          setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
          fetchData();
        }}
        filters={filters}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedValues={tempSelectedValues}
        setSelectedValues={setTempSelectedValues}
      />
    </ListPageLayout>
  );
};

export default DoctorClinicSpeciality;
